import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Coins, MapPin, Receipt, Smartphone, Zap } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Kicker, SectionHead } from "@/components/landing/Section";
import { AMENITIES, HOST_SIGNUP } from "@/config/business";

const Num = ({ n }) => (
  <span className="w-8 h-8 shrink-0 rounded-full bg-moss text-parchment font-extrabold grid place-items-center text-sm">{n}</span>
);

const StepList = ({ steps, testId }) => (
  <ol className="space-y-4" data-testid={testId}>
    {steps.map((s, i) => (
      <li key={s} className="flex gap-4 items-start">
        <Num n={i + 1} />
        <span className="pt-1 text-ink font-semibold leading-relaxed">{s}</span>
      </li>
    ))}
  </ol>
);

export const Ways = () => {
  const { t } = useLang();
  const h = t.pages.hosts;
  const icons = [Coins, MapPin];
  return (
    <section data-testid="hosts-ways">
      <SectionHead kicker={h.waysKicker} title={h.waysTitle} />
      <div className="mt-10 grid md:grid-cols-2 gap-6">
        {h.ways.map((w, i) => {
          const Icon = icons[i];
          return (
            <article key={w.title} data-testid={`hosts-way-${i}`} className="journal-card p-7 md:p-9">
              <div className="flex items-center justify-between gap-3">
                <span className="w-12 h-12 rounded-2xl bg-moss/10 text-moss grid place-items-center"><Icon className="w-6 h-6" /></span>
                <span className={`rounded-full px-3 py-1 text-xs font-extrabold ${i === 0 ? "bg-amber/15 text-amber-dark" : "bg-moss/10 text-moss"}`}>{w.tag}</span>
              </div>
              <h3 className="mt-5 font-display text-2xl font-semibold text-ink">{w.title}</h3>
              <p className="mt-3 text-ink-muted leading-relaxed">{w.body}</p>
            </article>
          );
        })}
      </div>
      <p className="mt-6 font-display italic text-lg text-rowan">{h.both}</p>
    </section>
  );
};

export const Join = () => {
  const { t } = useLang();
  const h = t.pages.hosts;
  const amenities = t.pages.signup.amenities;
  return (
    <section data-testid="hosts-join">
      <SectionHead kicker={h.joinKicker} title={h.joinTitle} />
      <div className="mt-10 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-6 space-y-8">
          <StepList steps={h.steps} testId="hosts-steps" />
          <div className="rounded-[1.5rem] border-2 border-dashed border-line p-6" data-testid="hosts-pit-stop">
            <h3 className="font-display text-xl font-semibold text-ink inline-flex items-center gap-2"><MapPin className="w-5 h-5 text-rowan" /> {h.pitTitle}</h3>
            <p className="mt-2 text-ink-muted leading-relaxed">{h.pitBody}</p>
          </div>
        </div>
        <div className="lg:col-span-6 journal-card p-7 md:p-8" data-testid="hosts-form-card">
          <h3 className="font-display text-xl font-semibold text-ink">{h.formTitle}</h3>
          <dl className="mt-4 divide-y divide-line">
            {h.fields.map(([name, note]) => (
              <div key={name} className="flex justify-between gap-4 py-2.5">
                <dt className="font-bold text-ink">{name}</dt>
                <dd className="text-sm text-ink-muted text-right">{note}</dd>
              </div>
            ))}
          </dl>
          <h4 className="mt-6 text-xs font-extrabold uppercase tracking-[0.14em] text-ink-muted">{h.amenitiesTitle}</h4>
          <ul className="mt-3 flex flex-wrap gap-2" data-testid="hosts-amenities">
            {AMENITIES.map((k) => (
              <li key={k} className="inline-flex items-center gap-1 rounded-full border border-line bg-parchment px-3 py-1 text-sm font-semibold text-ink">
                {k.endsWith("_charging") && <Zap className="w-3.5 h-3.5 text-amber-dark" />} {amenities[k]}
              </li>
            ))}
          </ul>
          <p className="mt-6 flex gap-2 font-bold text-moss"><Check className="w-5 h-5 shrink-0" /> {h.noWait}</p>
          <div className="mt-6 pt-6 border-t border-line">
            <p className="text-sm text-ink-muted leading-relaxed">{h.webNote}</p>
            <Link to={HOST_SIGNUP} data-testid="hosts-form-register" className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber hover:bg-amber-dark text-parchment px-6 py-3 font-extrabold transition-colors">
              {h.ctaHost} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export const TrollKroner = () => {
  const { t } = useLang();
  const h = t.pages.hosts;
  return (
    <section data-testid="hosts-tk" className="rounded-[2rem] bg-ink text-parchment p-8 sm:p-10 md:p-14 grid lg:grid-cols-2 gap-10 items-center">
      <div>
        <Kicker className="!text-amber">{h.tkKicker}</Kicker>
        <p className="mt-5 font-display text-5xl md:text-6xl font-semibold tracking-tight" data-testid="hosts-tk-rate">{h.tkTitle}</p>
        <p className="mt-6 text-parchment/80 leading-relaxed">{h.tkRule}</p>
      </div>
      <div>
        <h3 className="font-display text-xl font-semibold">{h.tkEarnTitle}</h3>
        <ul className="mt-4 space-y-3">
          {h.tkEarn.map((e) => (
            <li key={e} className="flex gap-3 text-parchment/90"><Coins className="w-5 h-5 shrink-0 text-amber" /> {e}</li>
          ))}
        </ul>
      </div>
    </section>
  );
};

const ReceiptCard = () => {
  const { t } = useLang();
  const h = t.pages.hosts;
  const ex = h.example;
  const rows = [[ex.total, "85 kr"], [ex.tk, "−60 TK"]];
  return (
    <div data-testid="hosts-receipt" className="journal-card p-7 md:p-8 rotate-[0.6deg]">
      <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-amber-dark"><Receipt className="w-4 h-4" /> {h.exampleTitle}</div>
      <p className="mt-2 text-ink-muted">{ex.note}</p>
      <dl className="mt-5 space-y-2 font-semibold text-ink tabular-nums">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-4"><dt>{label}</dt><dd>{value}</dd></div>
        ))}
        <div className="flex justify-between gap-4 pt-3 mt-3 border-t-2 border-dashed border-line font-extrabold text-lg"><dt>{ex.rest}</dt><dd data-testid="hosts-receipt-rest">25 kr</dd></div>
      </dl>
      <p className="mt-6 flex gap-2 text-sm text-ink-muted"><Smartphone className="w-4 h-4 shrink-0 mt-0.5 text-moss" /> {h.dashboard}</p>
    </div>
  );
};

export const Payment = () => {
  const { t } = useLang();
  const h = t.pages.hosts;
  return (
    <section data-testid="hosts-payment">
      <SectionHead kicker={h.payKicker} title={h.payTitle} />
      <div className="mt-10 grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7"><StepList steps={h.paySteps} testId="hosts-pay-steps" /></div>
        <div className="lg:col-span-5"><ReceiptCard /></div>
      </div>
    </section>
  );
};
